import type { AxiosError } from "axios";
import type {
  CreateSlaPolicyInput,
  SlaPolicyResponse,
  SlaTrackingData,
  UpdateSlaPolicyInput,
} from "shared";
import { axiosClient, slaAxiosClient } from "utilz";
import { apiErrorHandler } from "utilz/helpers";

interface GetSlaPoliciesParams {
  page?: number;
  perPage?: number;
  sortColumn?: string;
  sortDirection?: string;
  search?: string;
  filter?: Record<string, unknown>;
}

export interface SlaPolicyListResponse {
  data: SlaPolicyResponse[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
}

/**
 * Fetch all SLA policies from SLA service directly.
 */
export const getAllSlaPolicies = async ({
  page = 1,
  perPage = 50,
  sortColumn = "created_at",
  sortDirection = "DESC",
  search,
  filter,
}: GetSlaPoliciesParams = {}): Promise<SlaPolicyListResponse> => {
  try {
    const params: Record<string, string | number> = {
      page,
      perPage,
      sortColumn,
      sortDirection,
    };
    if (search) params.search = search;
    if (filter && Object.keys(filter).length > 0) params.filters = JSON.stringify(filter);

    const response = await slaAxiosClient.get("/api/v1/sla/policies", {
      params,
    });
    const resData = response.data;
    if (resData.success) {
      return resData.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError);
    throw error;
  }
};

/**
 * Get a single SLA policy by ID from SLA service.
 */
export const getSlaPolicyById = async (
  policyId: string,
): Promise<SlaPolicyResponse> => {
  try {
    const response = await slaAxiosClient.get(
      `/api/v1/sla/policies/${policyId}`,
    );
    const resData = response.data;
    if (resData.success) {
      return resData.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError);
    throw error;
  }
};

/**
 * Create a new SLA policy via SLA service.
 */
export const createSlaPolicy = async (
  data: CreateSlaPolicyInput,
): Promise<SlaPolicyResponse> => {
  try {
    const response = await slaAxiosClient.post("/api/v1/sla/policies", data);
    const resData = response.data;
    if (resData.success) {
      return resData.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError);
    throw error;
  }
};

/**
 * Update an existing SLA policy via SLA service.
 */
export const updateSlaPolicy = async ({
  policyId,
  ...data
}: UpdateSlaPolicyInput): Promise<SlaPolicyResponse> => {
  try {
    const response = await slaAxiosClient.put(
      `/api/v1/sla/policies/${policyId}`,
      data,
    );
    const resData = response.data;
    if (resData.success) {
      return resData.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError);
    throw error;
  }
};

export interface DeleteSlaPoliciesInput {
  policyIds: string[];
  newDefaultPolicyId?: string;
}

/**
 * Bulk delete SLA policies via SLA service.
 * Optionally accepts a replacement default policy ID when the default is being deleted.
 */
export const deleteSlaPolicies = async (
  input: DeleteSlaPoliciesInput,
): Promise<void> => {
  try {
    const response = await slaAxiosClient.delete("/api/v1/sla/policies", {
      data: {
        ids: input.policyIds,
        ...(input.newDefaultPolicyId
          ? { newDefaultPolicyId: input.newDefaultPolicyId }
          : {}),
      },
    });
    const resData = response?.data;
    if (resData?.success) {
      return;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError);
    throw error;
  }
};

/**
 * Toggle SLA policy active/inactive via SLA service.
 */
export const toggleSlaPolicy = async (
  policyId: string,
): Promise<SlaPolicyResponse> => {
  try {
    const response = await slaAxiosClient.patch(
      `/api/v1/sla/policies/${policyId}/toggle`,
    );
    const resData = response.data;
    if (resData.success) {
      return resData.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError);
    throw error;
  }
};

/**
 * Promote an SLA policy to the account-wide default. The backend handles
 * demoting the previous default atomically so no extra call is required.
 */
export const setSlaPolicyAsDefault = async (
  policyId: string,
): Promise<SlaPolicyResponse> => {
  try {
    const response = await slaAxiosClient.patch(
      `/api/v1/sla/policies/${policyId}/set-default`,
    );
    const resData = response.data;
    if (resData.success) {
      return resData.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError);
    throw error;
  }
};

/**
 * Get the effective default SLA policy for a specific customer.
 * Returns the customer-level override if set, otherwise falls back to the
 * MSP-wide default, or null when neither exists.
 */
export const getCustomerEffectiveDefault = async (
  customerId: string,
): Promise<{
  customerId: string;
  effectiveDefaultPolicyId: string | null;
  source: "customer_level" | "msp_level" | "none";
}> => {
  try {
    const response = await slaAxiosClient.get(
      `/api/v1/sla/policies/customers/${customerId}/effective-default`,
    );
    const resData = response.data;
    if (resData.success) {
      return resData.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError);
    throw error;
  }
};

/**
 * Get SLA tracking status for a single ticket.
 * Stays on webservice (proxies to SLA service internally).
 */
export const getTicketSlaStatus = async (
  ticketId: string,
): Promise<SlaTrackingData | null> => {
  try {
    const url = `/v2/servicedesk/tickets/${ticketId}/sla-status`;
    const response = await axiosClient.get(url);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    }
    throw response;
  } catch (error) {
    apiErrorHandler(error as AxiosError);
    return null;
  }
};
